interface IBundleOwnerScope {
  __E3D_OWNER__?: { version: string };
}

/**
 * Single-owner guard for the web-component runtime.
 *
 * A host page must load exactly one 3D Exhibition bundle. A second bundle would overwrite
 * `globalThis.E3D` (last-write-wins) and throw an uncaught `NotSupportedError` on the first
 * duplicate `customElements.define`, leaving a half-initialised runtime. This claims
 * ownership on first load and refuses — loudly, without throwing — on any later load, so the
 * first bundle keeps ownership.
 *
 * @returns `true` if this bundle may install the runtime, `false` if another bundle owns the page.
 */
export function claimBundleOwnership(
  version: string,
  scope: IBundleOwnerScope = globalThis as unknown as IBundleOwnerScope
): boolean {
  const existing = scope.__E3D_OWNER__;
  if (existing) {
    // Raw console.error, not the app logger: the logger is gated off at the default
    // production log level, and a live host page loading two bundles is exactly the
    // production case this diagnostic must reach.
    console.error(
      `[E3D] A 3D Exhibition runtime (version ${existing.version}) is already installed on this page. ` +
        `Refusing to install a second bundle (version ${version}). Load exactly one bundle per page.`
    );
    return false;
  }
  scope.__E3D_OWNER__ = { version };
  return true;
}

/**
 * Registers a custom element only when the name is not already defined, so a duplicate
 * registration degrades gracefully instead of throwing mid-module-evaluation.
 */
export function defineElementOnce(
  name: string,
  ctor: CustomElementConstructor,
  registry: CustomElementRegistry = customElements
): void {
  if (registry.get(name)) {
    // Skip instead of throwing, but stay observable: on a third-party host page a
    // pre-existing registration for one of our tag names (another bundle that did not
    // claim ownership, or name-squatting) would otherwise be silently honored.
    console.error(`[E3D] Custom element "${name}" is already registered; skipping. Another script defined it first.`);
    return;
  }
  registry.define(name, ctor);
}
